/**
 * Stroke-SVG icon set, lucide-style. 20×20 viewBox, stroke-width 1.5,
 * uses currentColor. Per BRIEFING.md §3 — keine Emoji, Stroke-SVGs only.
 */

interface IconProps {
  size?: number;
  className?: string;
}

function svg(children: React.ReactNode, { size = 20, className }: IconProps) {
  return (
    <svg
      viewBox="0 0 20 20"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

/** Home — house with door */
export function IconHome(p: IconProps = {}) {
  return svg(
    <>
      <path d="M3 9.5L10 3l7 6.5V16a1.5 1.5 0 0 1-1.5 1.5h-11A1.5 1.5 0 0 1 3 16V9.5z" />
      <path d="M8 17.5v-4.5h4v4.5" />
    </>,
    p,
  );
}

/** Slides — projector / presentation screen */
export function IconSlides(p: IconProps = {}) {
  return svg(
    <>
      <rect x="2.5" y="3.5" width="15" height="10" rx="1.5" />
      <path d="M7 17h6M10 13.5V17" />
    </>,
    p,
  );
}

/** Themen — checklist clipboard */
export function IconTopics(p: IconProps = {}) {
  return svg(
    <>
      <rect x="4.5" y="3" width="11" height="14" rx="1.5" />
      <path d="M7.5 2.5h5v2h-5z" />
      <path d="M7.5 8.5h5M7.5 11.5h5M7.5 14.5h3" />
    </>,
    p,
  );
}

/** Anforderung — pen on document */
export function IconRequirement(p: IconProps = {}) {
  return svg(
    <>
      <path d="M5 3.5h6.5L15 7v9.5a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1v-13a1 1 0 0 1 1-1z" />
      <path d="M11.5 3.5V7H15" />
      <path d="M7.5 11.5l1.5 1.5 3.5-3.5" />
    </>,
    p,
  );
}

/** Ergebnis der Gruppen — people / users */
export function IconGroupResults(p: IconProps = {}) {
  return svg(
    <>
      <circle cx="7" cy="7" r="2.5" />
      <path d="M2.5 16.5c0-2.5 2-4.5 4.5-4.5s4.5 2 4.5 4.5" />
      <circle cx="14" cy="6.5" r="2" />
      <path d="M13 12c2.5 0 4.5 2 4.5 4.5" />
    </>,
    p,
  );
}

/** KI Bewertung — sparkle / spark */
export function IconAiEval(p: IconProps = {}) {
  return svg(
    <>
      <path d="M10 2.5l1.6 4.4L16 8.5l-4.4 1.6L10 14.5l-1.6-4.4L4 8.5l4.4-1.6z" />
      <path d="M15.5 14l.6 1.4L17.5 16l-1.4.6L15.5 18l-.6-1.4L13.5 16l1.4-.6z" />
    </>,
    p,
  );
}

/** KI Umsetzung — code / build artifact */
export function IconAiBuild(p: IconProps = {}) {
  return svg(
    <>
      <path d="M7 6.5L3.5 10 7 13.5" />
      <path d="M13 6.5L16.5 10 13 13.5" />
      <path d="M11.5 4.5l-3 11" />
    </>,
    p,
  );
}

/** Backend — sliders / settings */
export function IconBackend(p: IconProps = {}) {
  return svg(
    <>
      <path d="M3 6h8M14 6h3" />
      <circle cx="12.5" cy="6" r="1.5" />
      <path d="M3 14h3M9 14h8" />
      <circle cx="7.5" cy="14" r="1.5" />
    </>,
    p,
  );
}

/** Logout — door + arrow */
export function IconLogout(p: IconProps = {}) {
  return svg(
    <>
      <path d="M11 3.5H4.5v13H11" />
      <path d="M8 10h9.5M14.5 7L17.5 10 14.5 13" />
    </>,
    p,
  );
}

/** Chevron right */
export function IconChevronRight(p: IconProps = {}) {
  return svg(<path d="M7.5 4l6 6-6 6" />, p);
}

/** Chevron left */
export function IconChevronLeft(p: IconProps = {}) {
  return svg(<path d="M12.5 4l-6 6 6 6" />, p);
}

/** Check */
export function IconCheck(p: IconProps = {}) {
  return svg(<path d="M4.5 10.5l3 3 8-8" />, p);
}

/** Plus */
export function IconPlus(p: IconProps = {}) {
  return svg(<path d="M10 4v12M4 10h12" />, p);
}

/** Trash / delete */
export function IconTrash(p: IconProps = {}) {
  return svg(
    <>
      <path d="M3.5 5.5h13M8 3h4M5.5 5.5l1 11a1 1 0 0 0 1 .9h5a1 1 0 0 0 1-.9l1-11" />
      <path d="M8.5 8.5v6M11.5 8.5v6" />
    </>,
    p,
  );
}

/** Save / floppy */
export function IconSave(p: IconProps = {}) {
  return svg(
    <>
      <path d="M3.5 3.5h11l2 2v11a1 1 0 0 1-1 1h-12a1 1 0 0 1-1-1v-12a1 1 0 0 1 1-1z" />
      <path d="M5.5 3.5v4h7v-4M5.5 17.5v-5h9v5" />
    </>,
    p,
  );
}

/** Caret down */
export function IconCaretDown(p: IconProps = {}) {
  return svg(<path d="M5 7.5l5 5 5-5" />, p);
}

/** Caret up */
export function IconCaretUp(p: IconProps = {}) {
  return svg(<path d="M5 12.5l5-5 5 5" />, p);
}

/** Clock */
export function IconClock(p: IconProps = {}) {
  return svg(
    <>
      <circle cx="10" cy="10" r="7" />
      <path d="M10 6v4l2.5 2.5" />
    </>,
    p,
  );
}

/** Send / paper-plane (submit) */
export function IconSend(p: IconProps = {}) {
  return svg(
    <>
      <path d="M17 3L3.5 9.5l5.5 1.5L11 17z" />
      <path d="M9 11l8-8" />
    </>,
    p,
  );
}
